/**
 * Timing rules for the online-orders board (F-ONL-N4 / F-ONL-N5). Pure, so the
 * thresholds are pinned by tests rather than by eye.
 */

export type Urgency = 'calm' | 'warn' | 'late'

/**
 * An order nobody has answered is urgent within minutes, not the quarter-hour
 * a kitchen ticket gets: the customer is staring at "Order placed" and every
 * minute here is a minute the kitchen has not started.
 */
export const INCOMING_WARN_MIN = 2
export const INCOMING_LATE_MIN = 5

/** Kitchen-side ageing for an accepted order that has no promised time. */
export const KITCHEN_WARN_MIN = 15
export const KITCHEN_LATE_MIN = 30

export function incomingUrgency(elapsedMin: number): Urgency {
  if (elapsedMin >= INCOMING_LATE_MIN) return 'late'
  if (elapsedMin >= INCOMING_WARN_MIN) return 'warn'
  return 'calm'
}

export function kitchenUrgency(elapsedMin: number): Urgency {
  if (elapsedMin >= KITCHEN_LATE_MIN) return 'late'
  if (elapsedMin >= KITCHEN_WARN_MIN) return 'warn'
  return 'calm'
}

/** Whole minutes from `fromMs` to `toMs`, never negative. */
export function minutesBetween(fromMs: number, toMs: number): number {
  return Math.max(0, Math.floor((toMs - fromMs) / 60_000))
}

/** "12m", "1h 5m". */
export function formatMinutes(min: number): string {
  const m = Math.max(0, Math.round(min))
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`
}

export interface PromiseState {
  /** Minutes until the promise (positive) or since it passed (negative). */
  minutesLeft: number
  late: boolean
  urgency: Urgency
}

/**
 * Where an accepted order stands against the time promised on Accept. Amber in
 * the last five minutes, red once it has passed.
 */
export function promiseState(promisedAtMs: number, nowMs: number): PromiseState {
  const diff = Math.round((promisedAtMs - nowMs) / 60_000)
  const late = nowMs > promisedAtMs
  return { minutesLeft: diff, late, urgency: late ? 'late' : diff <= 5 ? 'warn' : 'calm' }
}

/** "12m left" / "due now" / "8m late". */
export function promiseLabel(s: PromiseState): string {
  if (s.late) return s.minutesLeft === 0 ? 'due now' : `${formatMinutes(-s.minutesLeft)} late`
  return s.minutesLeft <= 0 ? 'due now' : `${formatMinutes(s.minutesLeft)} left`
}

// ── Accept: the "ready in" stepper ───────────────────────────────────────────
export const READY_MIN = 5
export const READY_MAX = 240
export const READY_STEP = 5

/** Snap to the stepper's 5-minute grid inside 5–240. */
export function clampReadyMinutes(n: number): number {
  if (!Number.isFinite(n)) return 20
  const snapped = Math.round(n / READY_STEP) * READY_STEP
  return Math.min(READY_MAX, Math.max(READY_MIN, snapped))
}

/** What the promise means for this order — the doorstep for a delivery. */
export function readyInLabel(fulfillment: string): string {
  if (fulfillment === 'delivery') return 'At the door in'
  if (fulfillment === 'dine_in') return 'Served in'
  return 'Ready in'
}

// ── Nag ring ─────────────────────────────────────────────────────────────────
export const NAG_INTERVAL_MS = 15_000
/** Twice as often once any order has waited past INCOMING_LATE_MIN. */
export const NAG_INTERVAL_LATE_MS = 7_500

export function nagIntervalMs(oldestWaitMin: number | null): number {
  return oldestWaitMin != null && oldestWaitMin >= INCOMING_LATE_MIN ? NAG_INTERVAL_LATE_MS : NAG_INTERVAL_MS
}

// ── Pause / busy ─────────────────────────────────────────────────────────────
export const PAUSE_OPTIONS: readonly { minutes: number | null; label: string }[] = [
  { minutes: 15, label: '15 minutes' },
  { minutes: 30, label: '30 minutes' },
  { minutes: 60, label: '1 hour' },
  { minutes: null, label: 'Until I resume' },
]

export const BUSY_OPTIONS: readonly number[] = [10, 15, 30]

/** The minutes a busy spell lasts — long enough to clear the backlog, then it lifts itself. */
export const BUSY_DURATION_MIN = 60

/** "8:30 PM" in the viewer's clock (the till sits in the restaurant). */
export function clockTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}
